import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { apiService } from "../services/api";
import Loader from "../components/Loader";
import { ArrowLeftIcon, Upload, Trash2 } from "lucide-react";
import { BASE_URL, MEDIA_URL } from "../config";
import axios from "axios";

const ACCOMMODATION_TYPES = [
    { value: "1", label: "Hotel" },
    { value: "2", label: "Lodge" },
    { value: "3", label: "Guesthouse" },
    { value: "4", label: "Airbnb" },
];

const ROOM_TYPES = [
    { value: "single", label: "Single Sharing" },
    { value: "double", label: "Double Sharing" },
    { value: "triple", label: "Triple Sharing" },
    { value: "deluxe", label: "Deluxe" },
];

type RoomPricing = {
    single: string;
    double: string;
    triple: string;
    deluxe: string;
};

// Existing photo now tracks both DB id + url (needed for delete/replace)
type ExistingPhoto = { id: number; url: string } | null;

const RATINGS = ["1", "2", "3", "4", "5"];

const EditProperty = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [propData, setPropData] = useState<any>();

    const [regions, setRegions] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [amenities, setAmenities] = useState<any[]>([]);

    const [images, setImages] = useState<(File | null)[]>(Array(6).fill(null));
    const [imagePreviews, setImagePreviews] = useState<(string | null)[]>(Array(6).fill(null));
    const [existingImages, setExistingImages] = useState<ExistingPhoto[]>(Array(6).fill(null));

    // Which slot is currently uploading/deleting (for loading state)
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

    const [showSuccess, setShowSuccess] = useState(false);


    const [formData, setFormData] = useState<any>({
        ownerId: "",
        propertyName: "",
        accommodationId: "",
        regionId: "",
        districtId: "",
        address: "",
        contactName: "",
        Phone: "",
        description: "",
        basePrice: "",
        rating: "",
        totalRooms: "",
        status: "",
        mapUrl: "",
        amenities: [],
        roomTypes: [],
        roomPricing: {
            single: "",
            double: "",
            triple: "",
            deluxe: "",
        },
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                await Promise.all([loadDropdowns(), fetchProperty()]);
            } catch (err) {
                console.error("[loadData] Error:", err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        if (formData.regionId) {
            apiService.getDistricts(formData.regionId).then(setDistricts);
        }
    }, [formData.regionId]);

    const loadDropdowns = async () => {
        const regionData = await apiService.getRegions();
        const amenityData = await apiService.getAmenities();
        setRegions(regionData);
        setAmenities(amenityData);
    };

    const fetchProperty = async () => {
        try {
            const res = await apiService.getHotelById(id!);
            const data = res.data;
            setPropData(data);
            const pricing: RoomPricing = {
                single: "",
                double: "",
                triple: "",
                deluxe: "",
            };

            const types: string[] = [];

            if (data.rooms && data.rooms.length > 0) {
                data.rooms.forEach((room: any) => {
                    const type = room.roomType;

                    if (type) {
                        types.push(type);
                        pricing[type as keyof RoomPricing] = String(room.price);
                    }
                });
            }

            setFormData({
                propertyName: data.propName || "",
                accommodationId: data.accommodationType || "",
                regionId: data.regionId || "",
                districtId: data.districtId || "",
                address: data.address || "",
                contactName: data.contactName || "",
                phone: data.phone
                    ? data.phone.startsWith("+")
                        ? data.phone
                        : `+${data.phone}`
                    : "",
                description: data.description || "",
                basePrice: data.basePrice || "",
                rating: data.rating || "",
                totalRooms: data.totalRooms || "",
                status: data.status || "",
                mapUrl: data.mapUrl || "",
                amenities: data.amenities || [],
                roomTypes: types,
                roomPricing: pricing,
            });

            if (data.photos) {
                const previews = Array(6).fill(null);
                const existing: ExistingPhoto[] = Array(6).fill(null);

                data.photos.slice(0, 6).forEach((p: any, i: number) => {
                    const url = `${MEDIA_URL}${p.photo_url}`;
                    previews[i] = url;
                    existing[i] = { id: p.id, url: p.photo_url };
                });


                setImagePreviews(previews);
                setExistingImages(existing);
            }
        } catch (err) {
            console.error("[fetchProperty] Error:", err);
        }
    };

    const updateField = (field: string, value: any) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRegionChange = (regionId: string) => {
        updateField("regionId", regionId);
        updateField("districtId", "");
    };

    const toggleAmenity = (value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            amenities: prev.amenities.includes(value)
                ? prev.amenities.filter((a: string) => a !== value)
                : [...prev.amenities, value],
        }));
    };

    const toggleRoomType = (type: string) => {
        setFormData((prev: any) => ({
            ...prev,
            roomTypes: prev.roomTypes.includes(type)
                ? prev.roomTypes.filter((t: string) => t !== type)
                : [...prev.roomTypes, type],
        }));
    };

    const handleRoomPriceChange = (type: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            roomPricing: {
                ...prev.roomPricing,
                [type]: value,
            },
        }));
    };

    // New file selected — sync with backend immediately
    // (if a photo already existed in this slot, delete it first, then upload the new one)
    const handleImageChange = async (index: number, file: File | null) => {
        if (!file) return;


        const previews = [...imagePreviews];
        previews[index] = URL.createObjectURL(file);
        setImagePreviews(previews);

        setUploadingIndex(index);

        try {
            const existingAtIndex = existingImages[index];

            if (existingAtIndex) {
                await apiService.deletePropertyPhoto(existingAtIndex.id);
            
            }

            const res = await apiService.addPropertyPhoto(id!, file, undefined, index);
            const newExisting = [...existingImages];
            newExisting[index] = { id: res.data.id, url: res.data.photoUrl };
            setExistingImages(newExisting);

            const updatedImages = [...images];
            updatedImages[index] = null;
            setImages(updatedImages);

        } catch (err) {
            console.error("[handleImageChange] Failed to sync photo at index:", index, "Error:", err);
            alert("Failed to save photo. Please try again.");

            const revertedPreviews = [...imagePreviews];
            revertedPreviews[index] = existingImages[index]
                ? `${MEDIA_URL}${existingImages[index]!.url}`
                : null;
            setImagePreviews(revertedPreviews);
        } finally {
            setUploadingIndex(null);
        }
    };

    // Delete button — delete from backend immediately
    const handleRemoveImage = async (index: number) => {
        const existingAtIndex = existingImages[index];
        if (existingAtIndex) {
            const confirmDelete = window.confirm("Delete this photo permanently?");

            if (!confirmDelete) {
                return;
            }

            setUploadingIndex(index);

            try {
               
                const result = await apiService.deletePropertyPhoto(existingAtIndex.id);
            } catch (err) {
                console.error("[handleRemoveImage] Failed to delete photo id:", existingAtIndex.id, "Error:", err);
                alert("Failed to delete photo. Please try again.");
                setUploadingIndex(null);
                return;
            }

            setUploadingIndex(null);
        }

        const updatedImages = [...images];
        updatedImages[index] = null;
        setImages(updatedImages);

        const previews = [...imagePreviews];
        previews[index] = null;
        setImagePreviews(previews);

        const existing = [...existingImages];
        existing[index] = null;
        setExistingImages(existing);

    };

    // Photo upload/existingPhotos logic removed from submit
    // (photos now sync via their own add/delete API calls immediately)
    const handleSubmit = async (e: any) => {
        e.preventDefault();

        try {
            const payload = new FormData();

            payload.append("ownerId", propData.ownerId);
            payload.append("propertyId", propData.id);
            payload.append("propertyName", formData.propertyName);
            payload.append("accommodationId", formData.accommodationId);
            payload.append("regionId", formData.regionId);
            payload.append("districtId", formData.districtId);
            payload.append("address", formData.address);
            payload.append("contactName", formData.contactName);
            payload.append("phone", formData.phone);
            payload.append("description", formData.description);
            payload.append("basePrice", formData.basePrice);
            payload.append("rating", formData.rating);
            payload.append("totalRooms", formData.totalRooms);
            payload.append("status", formData.status);
            payload.append("mapUrl", formData.mapUrl);
            payload.append("amenities", JSON.stringify(formData.amenities));

            const pricingPayload = formData.roomTypes.map((type: string) => ({
                roomType: type,
                pricePerNight: Number(formData.roomPricing[type] || 0),
            }));

            payload.append("roomPricing", JSON.stringify(pricingPayload));

            await axios.post(`${BASE_URL}/properties/update`, payload, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setShowSuccess(true);

            setTimeout(() => {
                navigate(`/hotels/${id}`);
            }, 2000);
        } catch (err) {
            console.error("[handleSubmit] Error:", err);
            alert("Failed to update property. Please check the form and try again.");
        }
    };

    return (
        <AdminLayout>
            {loading ? (
                <div className="flex justify-center items-center h-[70vh]">
                    <Loader />
                </div>
            ) : (
                <div className="max-w-6xl mx-auto">

                    <div className="flex items-center gap-3 mb-6">
                        <button onClick={() => navigate(-1)}>
                            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
                        </button>
                        <h1 className="text-xl font-bold">Edit Property</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Property Name */}
                        <div>
                            <label className="text-sm font-medium">Property Name *</label>
                            <input
                                value={formData.propertyName}
                                onChange={(e) => updateField("propertyName", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />
                        </div>

                        {/* Address */}
                        <div>
                            <label className="text-sm font-medium">Address *</label>
                            <input
                                value={formData.address}
                                onChange={(e) => updateField("address", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />
                        </div>

                        {/* Contact Name */}
                        <div>
                            <label className="text-sm font-medium">Contact Name *</label>
                            <input
                                value={formData.contactName}
                                onChange={(e) => updateField("contactName", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="text-sm font-medium">Phone *</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^\d+]/g, "");
                                    updateField("phone", value);
                                }}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />
                        </div>

                        {/* Region + District */}
                        <div className="grid grid-cols-2 gap-4">

                            {/* Region */}
                            <div>
                                <label className="text-sm font-medium">Region *</label>

                                <select
                                    value={formData.regionId}
                                    onChange={(e) => handleRegionChange(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                >
                                    <option value="">Select</option>

                                    {regions.map((r) => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* District */}
                            <div>
                                <label className="text-sm font-medium">District *</label>

                                <select
                                    value={formData.districtId}
                                    onChange={(e) => updateField("districtId", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                >
                                    <option value="">Select</option>

                                    {districts.map((d) => (
                                        <option key={d.value} value={d.value}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        {/* Map URL */}
                        <div>
                            <label className="text-sm font-medium">Google Map URL</label>
                            <input
                                value={formData.mapUrl}
                                onChange={(e) => updateField("mapUrl", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {/* Accommodation Type */}
                            <div>
                                <label className="text-sm font-medium">Accommodation Type *</label>
                                <select
                                    value={formData.accommodationId}
                                    onChange={(e) => updateField("accommodationId", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                >
                                    <option value="">Select</option>
                                    {ACCOMMODATION_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Total Rooms */}
                            <div>
                                <label className="text-sm font-medium">Total Rooms *</label>

                                <input
                                    type="number"
                                    value={formData.totalRooms}
                                    onChange={(e) => updateField("totalRooms", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                />
                            </div>
                        </div>

                        {/* Rooms / Price / Rating */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Base Price */}
                            <div>
                                <label className="text-sm font-medium">
                                    Base Price (per night) *
                                </label>

                                <input
                                    type="number"
                                    value={formData.basePrice}
                                    onChange={(e) => updateField("basePrice", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                />
                            </div>

                            {/* Rating */}
                            <div>
                                <label className="text-sm font-medium">Rating *</label>

                                <select
                                    value={formData.rating}
                                    onChange={(e) => updateField("rating", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-1"
                                >
                                    <option value="">Select Rating</option>

                                    {RATINGS.map((r) => (
                                        <option key={r} value={r}>
                                            {r} Star
                                        </option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        {/* Room Types */}
                        <div>
                            <label className="text-sm font-medium">Room Types</label>

                            <div className="flex gap-3 mt-2">
                                {ROOM_TYPES.map((room) => {
                                    const active = formData.roomTypes.includes(room.value);

                                    return (
                                        <button
                                            key={room.value}
                                            type="button"
                                            onClick={() => toggleRoomType(room.value)}
                                            className={`px-4 py-2 rounded-full text-sm
                        ${active ? "bg-green-600 text-white" : "bg-gray-200"}
                      `}
                                        >
                                            {room.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {formData.roomTypes.length > 0 && (
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    {formData.roomTypes.map((type: string) => (
                                        <input
                                            key={type}
                                            type="number"
                                            placeholder={`${type} price`}
                                            value={formData.roomPricing[type]}
                                            onChange={(e) =>
                                                handleRoomPriceChange(type, e.target.value)
                                            }
                                            className="border rounded-lg px-3 py-2"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Amenities */}
                        <div className="grid grid-cols-2 gap-2">
                            {amenities.map((a) => (
                                <label key={a.value} className="flex gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.amenities.includes(a.value)}
                                        onChange={() => toggleAmenity(a.value)}
                                    />
                                    {a.label}
                                </label>
                            ))}
                        </div>

                        {/* Images */}
                        <div className="grid grid-cols-3 gap-3">
                            {imagePreviews.map((preview, i) => (
                                <div key={i} className="border rounded-lg p-2 relative">

                                    <input
                                        type="file"
                                        accept="image/*"
                                        disabled={uploadingIndex === i}
                                        onChange={(e) =>
                                            handleImageChange(i, e.target.files?.[0] || null)
                                        }
                                    />

                                    {uploadingIndex === i && (
                                        <p className="text-xs text-gray-500 mt-1">Saving...</p>
                                    )}

                                    {preview && (
                                        <>
                                            <img
                                                src={preview}
                                                className="mt-2 h-20 w-full object-cover"
                                            />

                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(i)}
                                                disabled={uploadingIndex === i}
                                                className="absolute top-1 right-1"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="border px-6 py-2 rounded-lg"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="bg-brand-700 text-white px-6 py-2 rounded-lg"
                            >
                                Update Property
                            </button>
                        </div>

                    </form>

                    {showSuccess && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
                            <div className="bg-white rounded-xl p-8 text-center w-[320px]">

                                <div className="flex justify-center mb-4">
                                    <div className="bg-green-100 p-3 rounded-full">
                                        <Upload className="text-green-600" size={28} />
                                    </div>
                                </div>

                                <h3 className="text-lg font-semibold">
                                    Property Updated
                                </h3>

                                <p className="text-sm text-gray-500 mt-2">
                                    Property successfully updated.
                                </p>

                            </div>
                        </div>
                    )}

                </div>
            )}
        </AdminLayout>
    );
};

export default EditProperty;